import type {
  SpiderChartAxis,
  SpiderChartSeries
} from '@components/spider-chart';

/**
 * The minimum a squad player needs to carry for the chart. Both the AI
 * recommendation and the user's planner squad satisfy this structurally, so
 * one builder serves both sides.
 */
export type SquadPlayerLike = {
  position: string;
  isCaptain?: boolean | null;
  predictedPoints?: number | null;
  fixtures?: Array<{
    gameweek: number;
    predictedPoints?: number | null;
  }> | null;
};

export type SquadLike = {
  starting: SquadPlayerLike[];
  bench?: SquadPlayerLike[] | null;
};

/**
 * Every axis is predicted points per player for one gameweek, so the whole
 * chart shares a single points scale and the rings can be labelled in points.
 * Per player rather than per group, because five midfielders would otherwise
 * dwarf one goalkeeper on every chart regardless of quality.
 */
export const AI_TEAM_SPIDER_AXES: SpiderChartAxis[] = [
  { key: 'gk', label: 'GK' },
  { key: 'def', label: 'DEF' },
  { key: 'mid', label: 'MID' },
  { key: 'fwd', label: 'FWD' },
  { key: 'captain', label: 'Captain' },
  { key: 'bench', label: 'Bench' }
];

/** Rings are labelled every two points. */
export const AI_TEAM_SPIDER_TICK_STEP = 2;
/** The chart never shrinks below this, so a weak week still reads as weak. */
const MINIMUM_SCALE_MAXIMUM = 8;

const POINTS_DECIMAL_PLACES = 1;

/**
 * Predicted points for one player in a single gameweek.
 *
 * The player-level `predictedPoints` scalar is not safe to compare between
 * squads: a free hit is a one-week chip, a wildcard is solved over a longer
 * horizon and the planner carries its own horizon, so a scalar covering more
 * gameweeks would flatter one side for no real reason. Summing the fixtures
 * for one named gameweek keeps every squad on the same basis and handles
 * double gameweeks (two fixtures) and blanks (none) correctly. The scalar is
 * only a fallback for when fixtures are absent.
 */
export const gameweekPoints = (
  player: SquadPlayerLike,
  gameweek: number | null
): number => {
  const fixtures = player.fixtures ?? [];
  if (gameweek === null || fixtures.length === 0) {
    return player.predictedPoints ?? 0;
  }
  return fixtures
    .filter((fixture) => fixture.gameweek === gameweek)
    .reduce((total, fixture) => total + (fixture.predictedPoints ?? 0), 0);
};

const sumPoints = (players: SquadPlayerLike[], gameweek: number | null) =>
  players.reduce(
    (total, player) => total + gameweekPoints(player, gameweek),
    0
  );

type GroupSummary = { total: number; perPlayer: number } | null;

/** Null when the group is empty, so the axis is a gap rather than a zero. */
const summariseGroup = (
  players: SquadPlayerLike[],
  gameweek: number | null
): GroupSummary => {
  if (players.length === 0) return null;
  const total = sumPoints(players, gameweek);
  return { total, perPlayer: total / players.length };
};

const formatPoints = (value: number): string =>
  `${value.toFixed(POINTS_DECIMAL_PLACES)} pts`;

const describeGroup = (summary: GroupSummary): string | null => {
  if (summary === null) return null;
  return summary.total === summary.perPlayer
    ? formatPoints(summary.total)
    : `${formatPoints(summary.total)} (${summary.perPlayer.toFixed(
        POINTS_DECIMAL_PLACES
      )} each)`;
};

export type AiTeamSpiderInput = {
  key: string;
  label: string;
  color: string;
  squad: SquadLike;
};

/**
 * Turns squads into spider chart series so the AI recommendation can be read
 * against the user's own team in one figure. Pass the gameweek every squad
 * should be judged on. Values are predicted points per player, not a 0-100
 * score, so pair the result with `resolveAiTeamScaleMaximum`.
 */
export const buildAiTeamSpiderSeries = (
  squads: AiTeamSpiderInput[],
  gameweek: number | null = null
): SpiderChartSeries[] =>
  squads.map(({ key, label, color, squad }) => {
    const starting = squad.starting ?? [];
    const bench = squad.bench ?? [];
    const byPosition = (position: string) =>
      summariseGroup(
        starting.filter((player) => player.position === position),
        gameweek
      );

    const groups: Record<string, GroupSummary> = {
      gk: byPosition('GK'),
      def: byPosition('DEF'),
      mid: byPosition('MID'),
      fwd: byPosition('FWD'),
      captain: summariseGroup(
        starting.filter((player) => player.isCaptain === true),
        gameweek
      ),
      bench: summariseGroup(bench, gameweek)
    };

    return {
      key,
      label,
      color,
      values: Object.fromEntries(
        AI_TEAM_SPIDER_AXES.map((axis) => [
          axis.key,
          groups[axis.key]?.perPlayer ?? null
        ])
      ),
      rawValues: Object.fromEntries(
        AI_TEAM_SPIDER_AXES.map((axis) => [
          axis.key,
          describeGroup(groups[axis.key] ?? null)
        ])
      )
    };
  });

/**
 * The outer ring, in points: the highest value on the chart rounded up to the
 * next tick, with a floor so a quiet gameweek is not stretched to fill the
 * chart and read as a strong one.
 */
export const resolveAiTeamScaleMaximum = (
  series: SpiderChartSeries[]
): number => {
  const highest = Math.max(
    0,
    ...series.flatMap((entry) =>
      Object.values(entry.values).filter(
        (value): value is number => value !== null
      )
    )
  );
  const rounded =
    Math.ceil(highest / AI_TEAM_SPIDER_TICK_STEP) * AI_TEAM_SPIDER_TICK_STEP;
  return Math.max(MINIMUM_SCALE_MAXIMUM, rounded);
};

/** Total predicted points for a squad's starting eleven in the gameweek. */
export const startingElevenPoints = (
  squad: SquadLike,
  gameweek: number | null
): number => sumPoints(squad.starting ?? [], gameweek);

export type AiTeamSpiderCaptionInput = {
  gameweek: number;
  aiLabel: string;
  aiPoints: number;
  /** Null when the user has no planner team to compare against. */
  yourPoints: number | null;
};

export const buildAiTeamSpiderCaption = ({
  gameweek,
  aiLabel,
  aiPoints,
  yourPoints
}: AiTeamSpiderCaptionInput): string => {
  const basis = `Predicted points per player for Gameweek ${gameweek}, starting eleven only except Bench.`;
  if (yourPoints === null) {
    return `${basis} ${aiLabel}: ${formatPoints(aiPoints)}.`;
  }
  return `${basis} ${aiLabel}: ${formatPoints(
    aiPoints
  )}. Your team: ${formatPoints(yourPoints)}.`;
};
